import { gql, useQuery } from '@apollo/client';
import React from 'react';

import { ListingCard } from './ListingCard';
import Button from '../atoms/button';
import { client } from '../../apollo-client';
import { mergeListingItems } from '@lib/listings/merge';
import { normaliseWordPressListingItem } from '@lib/listings/normalise';
import type { WordPressPostsData } from '@lib/listings/normalise';
import type { ListingItem } from '@lib/listings/types';

const SKELETON_CARD_COUNT = 10;
// Apollo needs a parseable document even when the query is skipped.
const NO_WORDPRESS_QUERY = 'query NoWordPressPosts { __typename }';

type ArticleResultsListProps = {
  /** Server-rendered WordPress page, shown until the client query answers. */
  initialData?: WordPressPostsData;
  /** WordPress GraphQL query; omit when the scope has no WordPress posts. */
  query?: string;
  /** Sanity items for the same scope, interleaved by date with WordPress. */
  sanityItems?: ListingItem[];
  onArticleClick?: () => void;
  articleClassName?: string;
  type?: string;
  category?: unknown;
};

const ArticleResultsList = ({
  query,
  initialData,
  sanityItems,
  onArticleClick,
  articleClassName
}: ArticleResultsListProps) => {
  const QUERY = gql`
    ${query ?? NO_WORDPRESS_QUERY}
  `;
  const { data, loading, fetchMore } = useQuery<WordPressPostsData>(QUERY, {
    client,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: !query
  });

  const wordPressData = data ?? initialData;
  const edges = wordPressData?.posts?.edges;
  const isWordPressPending = Boolean(query) && !edges;
  const wordPressItems = (edges ?? []).map((edge) =>
    normaliseWordPressListingItem(edge.node)
  );
  const items = mergeListingItems(sanityItems, wordPressItems);
  const pageInfo = wordPressData?.posts?.pageInfo;

  return (
    <div className="relative mx-auto max-w-7xl">
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3 lg:gap-6">
        {isWordPressPending && items.length === 0
          ? Array.from({ length: SKELETON_CARD_COUNT }).map((_, index) => (
              <ListingCard key={index} className={articleClassName} />
            ))
          : items.map((item) => (
              <ListingCard
                key={item.href}
                item={item}
                onArticleClick={onArticleClick}
                className={articleClassName}
              />
            ))}
        {pageInfo?.hasNextPage && (
          <div className="my-10 border-none">
            <Button
              variant="primary"
              onClick={() => {
                fetchMore({ variables: { cursor: pageInfo.endCursor } });
              }}
              loading={loading}
            >
              View More Articles
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArticleResultsList;
